import { useNavigate } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import useIsMobile from "../hooks/useIsMobile";
import useAuth from "../hooks/useAuth";

const REPORTS = [
  {
    title: "Daily Contractor Output",
    description: "Daily count of Active records grouped by Agency, with date columns and totals.",
    path: "/reports/contractor-output",
    permission: "reports.daily_contractor",
    icon: (
      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    title: "Daily TL Report",
    description: "Daily completed job orders per Team Leader.",
    path: "/reports/daily-tl-output",
    permission: "reports.daily_tl",
    icon: (
      <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    title: "Daily NI Teams Output",
    description: "Daily completed New Installation job orders per dispatched Team.",
    path: "/reports/daily-team-output",
    permission: "reports.daily_team",
    icon: (
      <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  {
    title: "Daily NI Personnel Output",
    description: "Daily completed New Installation job orders per individual Personnel member.",
    path: "/reports/daily-personnel-output",
    permission: "reports.daily_personnel",
    icon: (
      <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    title: "Daily Migration Teams Output",
    description: "Daily completed Migration job orders per dispatched Team.",
    path: "/reports/daily-migration-team-output",
    permission: "reports.daily_migration_team",
    icon: (
      <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h10M7 12h10M7 17h6M4 4h16v16H4z" />
      </svg>
    ),
  },
  {
    title: "Daily Migration Personnel Output",
    description: "Daily completed Migration job orders per individual Personnel member.",
    path: "/reports/daily-migration-personnel-output",
    permission: "reports.daily_migration_personnel",
    icon: (
      <svg className="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7a3 3 0 116 0 3 3 0 01-6 0zm-4 12a7 7 0 0114 0H5zM17 9h4m-2-2v4" />
      </svg>
    ),
  },
  {
    title: "Daily NAP Usage",
    description: "NAP codes used by completed NI & Migration jobs, with vacant-port readings — find NAPs with free ports.",
    path: "/reports/nap-usage",
    permission: "reports.nap_usage",
    icon: (
      <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
      </svg>
    ),
  },
  {
    title: "Output Per City",
    description: "Daily count of Active records grouped by City, filterable by Agency and City.",
    path: "/reports/city-output",
    permission: "reports.output_municipality",
    icon: (
      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    title: "PDS/ADS",
    description: "Permanently/Automatically Disconnected Subscribers. Import & search for potential returning subscribers.",
    path: "/reports/pds-ads",
    permission: "page.pds_ads",
    icon: (
      <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636a9 9 0 11-12.728 0M12 3v9" />
      </svg>
    ),
  },
  {
    title: "Scorecard",
    description: "Weekly scorecard of individual FST/Team performance based on Done, Cancelled, Rescheduled, and Attachment Compliance.",
    path: "/reports/scorecard",
    permission: "reports.scorecard",
    icon: (
      <svg className="w-8 h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.1 10.1c-.783-.57-.38-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.519-4.674z" />
      </svg>
    ),
  },
];

export default function ReportsPage() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const visible = REPORTS.filter((r) => hasPerm(r.permission));

  return (
    <div className="min-h-screen bg-gray-50">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Reports</h1>

        {visible.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            You do not have permission to view any reports.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((r) => (
            <button
              key={r.path}
              onClick={() => navigate(r.path)}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6 text-left flex flex-col gap-3 border border-transparent hover:border-blue-200"
            >
              {r.icon}
              <h2 className="text-lg font-semibold text-gray-800">{r.title}</h2>
              <p className="text-sm text-gray-500">{r.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

